import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  NavigateFunction,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Box, Grid, Radio, Typography } from "@mui/material";
import { routeName } from "../../../../routes/routeName";
import { advisorEstimateScoreApi } from "../../../../api/advisor-estimate-score/advisor-estimate.api";
import NavigateListCustom from "../../../../components/navigation/NavigationListCustom";
import { colors } from "../../../../constants/colors";
import InputTextField from "../../../../components/input/inputTextField";
import { ButtonOutlined } from "../../../../components/button/ButtonCustoms";

type Props = {};

const TeacherEvaluationScoresEstimateDetail: React.FC<Props> = (
  props: Props
) => {
  const navigate: NavigateFunction = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const prop_year = queryParams.get("year");
  const prop_term = queryParams.get("term");
  const prop_employee_name = queryParams.get("employee_name");
  const prop_employee_no = queryParams.get("employee_no");
  const prop_id_employee = queryParams.get("id_employee");
  const prop_course = queryParams.get("course");
  const prop_id_course_of_study = queryParams.get("id_course_of_study");
  const prop_id_course_annual = queryParams.get("id_course_annual");
  const prop_student_name = queryParams.get("student_name");

  const [dataDetail, setDataDetail] = useState<any>({});

  const getData = async (id: number) => {
    let query: any = {};

    const res = await advisorEstimateScoreApi.getScoreWithIdAdvisorEstimate(
      +id,
      query
    );
    const data = res.data;
    if (data.status < 300) {
      setDataDetail(res.data?.data ?? []);
    } else {
      setDataDetail([]);
    }
  };

  useEffect(() => {
    if (id) {
      getData(+id);
    }
  }, [id]);

  const optionEstimate = [
    {
      name: "น้อยที่สุด (1)",
      points: "1",
    },
    {
      name: "น้อย (2)",
      points: "2",
    },
    {
      name: "ปานกลาง (3)",
      points: "3",
    },
    {
      name: "มาก (4)",
      points: "4",
    },
    {
      name: "มากที่สุด (5)",
      points: "5",
    },
  ];

  // Grouping the data by `advisor_form_detail_name`
  const groupedData = dataDetail?.advisor_estimate_detail?.reduce(
    (acc: any, item: any) => {
      if (!acc[item.advisor_form_detail_name]) {
        acc[item.advisor_form_detail_name] = [];
      }
      acc[item.advisor_form_detail_name].push(item);
      return acc;
    },
    {}
  );

  return (
    <div>
      <NavigateListCustom
        navigateData={[
          {
            name: `คะแนนประเมินอาจารย์ผู้สอน`,
            key: "1",
            pathName: routeName.advisor.TeacherEvaluationScores,
            onClick: () =>
              navigate(`${routeName.advisor.TeacherEvaluationScores}`),
          },
          {
            name: `คะแนนประเมินอาจารย์ผู้สอน ปีการศึกษา ${prop_year} ภาคเรียนที่ ${prop_term}`,
            key: "2",
            pathName: routeName.advisor.TeacherEvaluationScores,
            onClick: () =>
              navigate(
                `${routeName.advisor.TeacherEvaluationScores}/${prop_id_course_of_study}/detail?year=${prop_year}&term=${prop_term}`
              ),
          },
          {
            name: `${prop_employee_name} ${prop_employee_no}`,
            key: "3",
            pathName: "",
            onClick: () =>
              navigate(
                routeName.advisor.TeacherEvaluationScores +
                  `/${prop_id_course_of_study}/course-detail/${prop_id_employee}/employee?employee_no=${prop_employee_no}&employee_name=${prop_employee_name}&year=${prop_year}&term=${prop_term}&id_course_of_study=${prop_id_course_of_study}&course=${prop_course}`
              ),
          },
          {
            name: `${prop_student_name}`,
            key: "4",
            pathName: "",
            onClick: () =>
              navigate(
                routeName.advisor.TeacherEvaluationScores +
                  `/${prop_id_course_annual}/course-annual-student-estimate?employee_no=${prop_employee_no}&employee_name=${prop_employee_name}&year=${prop_year}&term=${prop_term}&id_employee=${prop_id_employee}&id_course_of_study=${prop_id_course_of_study}&course=${prop_course}&id_course_annual=${prop_id_course_annual}`
              ),
          },
          {
            name: `${prop_course}`,
            key: "5",
            pathName: "",
          },
        ]}
      />

      {/* header */}
      <section className="my-4">
        <Typography variant="h3">{`${prop_student_name}`}</Typography>
      </section>

      {/*  */}
      <section className="my-4 mt-4">
        <Box textAlign="center">
          <Typography variant="h6">คะแนนเฉลี่ย</Typography>

          <Typography
            variant="h1"
            fontWeight="bold"
            sx={{
              color: colors.orange,
            }}
          >
            {parseFloat(dataDetail.points).toFixed(1)}
          </Typography>
        </Box>
      </section>

      {/* header */}
      <section className="my-6">
        <Typography variant="h4">{`${
          dataDetail?.advisor_form_name ?? "-"
        }`}</Typography>
      </section>

      <section className="my-6">
        {groupedData &&
          Object.entries(groupedData).map(([category, subcategories]: any) => (
            <Box key={category} mb={3}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={7}>
                  <Typography variant="h6" fontWeight="bold">
                    {category}
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Grid container spacing={1} justifyContent="space-between">
                    {optionEstimate.map((option) => (
                      <Grid item xs={2.4} key={option.points}>
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <Typography variant="body2">{option.name}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>

              {/* Subcategories */}
              {subcategories.map((item: any) => (
                <Grid
                  container
                  key={item.id_advisor_estimate_detail}
                  spacing={2}
                  alignItems="center"
                >
                  <Grid item xs={7}>
                    <Typography
                      variant="body1"
                      sx={{
                        marginLeft: 4,
                      }}
                    >
                      {item.advisor_form_detail_sub_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Grid container spacing={1} justifyContent="space-between">
                      {optionEstimate.map((option) => (
                        <Grid item xs={2.4} key={option.points}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                          >
                            <Radio
                              disabled
                              checked={+item.estimate_point === +option.points}
                              value={item.estimate_point}
                              name="radio-buttons"
                              inputProps={{ "aria-label": "A" }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Box>
          ))}
      </section>

      <article className="col-span-12 sm:col-span-12 md:col-span-12 lg:col-span-12 space-y-2">
        <InputTextField
          value={dataDetail.remark}
          heading="รายละเอียด"
          disabled
          row={3}
          maxRows={5}
          multiline
          inputHeight={"auto"}
        />
      </article>

      <div
        style={{
          display: "flex",
          gap: "10px",
          justifyContent: "center",
          marginTop: "20px",
        }}
      >
        <ButtonOutlined
          onClick={() => navigate(-1)}
          mode="del"
          buttonName="ย้อนกลับ"
        />
      </div>
    </div>
  );
};

export default TeacherEvaluationScoresEstimateDetail;
